// src/utils/activityLog.js
// Shared utility for writing activity/audit log entries to Firestore via the backend API.
// Logs are stored permanently in the Firestore `activityLogs` collection.
//
// Usage:
//   import { writeActivityLog } from '../utils/activityLog';
//   writeActivityLog({
//     source: 'apply_loan',
//     memberId: 'S00123',
//     memberName: 'Jane Doe',
//     // ...any extra fields relevant to that source
//   });
//
// Adding a new log source in future:
//   1. Call writeActivityLog({ source: 'your_new_source', ...fields }) from the new page.
//   2. Add the source label to SOURCE_LABELS below so the log page displays it nicely.
//   3. Done — no changes needed to ActivityLogPage or the backend.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Human-readable labels for each source value.
// Add new entries here when adding a new log source.
export const SOURCE_LABELS = {
  monthly_processing_adjust: 'Monthly Processing – Adjust',
  apply_loan:                 'Apply Loan',
  apply_loan_topup:           'Apply Loan – Top-Up',
  historical_loan_first:      'Historical Import – First Loan',
  historical_loan_topup:      'Historical Import – Top-Up',
  historical_loan_excel:      'Historical Import – Excel Bulk',
  manage_shares_plan:         'Manage Shares – Plan Update',
  manage_shares_deposit:      'Manage Shares – Deposit',
  devtools:                   'DevTools – Direct Operation',
};

/**
 * Write a log entry to Firestore via the backend API.
 * Fire-and-forget — never throws, never blocks the caller.
 * changedBy is resolved server-side from the auth token; no need to pass it.
 *
 * @param {Object} entry  Must include `source`. All other fields are optional.
 */
export async function writeActivityLog(entry) {
  try {
    const token = localStorage.getItem('hopeSaccoToken');
    await fetch(`${API_URL}/logs/activity`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(entry),
    });
    // Response is intentionally ignored — logging must never block or error the UI
  } catch {
    // Network error or server down — fail silently
  }
}
